import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HydraCollection } from './hydra/hydra-collection';
import { ResourceCollection } from './collection';
import { ResourceCollectionFactory } from './factory/resource-collection-factory';

@Injectable({
  providedIn: 'root'
})
export abstract class ApiService {
  protected readonly apiUrl: string;

  abstract readonly resourcePath: string;

  protected constructor(
    protected http: HttpClient
  ) {
    this.apiUrl = environment.apiUrl;
  }

  protected get resourceUrl(): string {
    return `${this.apiUrl}/${this.resourcePath}`;
  }

  private static queryParams(filters: Record<string, string>): string {
    const urlSearchParams = new URLSearchParams(filters);
    const queryString = urlSearchParams.toString();

    return queryString ? '?' + queryString : '';
  }

  private removeUrlFromId(id: string): string {
    return id.replace(this.resourcePath, '').replace(/\//g, '');
  }

  protected restList<T>(filters: Record<string, string> = {}): Observable<HydraCollection<T>> {
    return this.http.get<HydraCollection<T>>(this.resourceUrl + ApiService.queryParams(filters)).pipe(
      map(response => new HydraCollection<T>(response))
    );
  }

  protected restRead<T>(id: string): Observable<T> {
    return this.http.get<T>(this.resourceUrl + '/' + this.removeUrlFromId(id));
  }
  protected restReadItem<T>(id: string): Observable<T> {
    return this.http.get<T>(this.resourceUrl + '/' + id);
  }

  protected restCreate<T>(data: object): Observable<T> {
    return this.http.post<T>(this.resourceUrl, data);
  }

  protected restEnrolmentCreate<T>(data: object, idempotencyKey?: string): Observable<T> {
    const options = idempotencyKey ? { headers: new HttpHeaders({ 'Idempotency-Key': idempotencyKey }) } : {};
    return this.http.post<T>(this.resourceUrl + "/create", data, options);
  }

  protected restPresenceUpdate<T>(id: string, endpoint:string, data: object): Observable<T> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/merge-patch+json'
      })
    };

    return this.http.patch(`${this.resourceUrl}/${id}`, data, httpOptions) as Observable<T>;
  }


  protected restUpdate<T>(id: string, data: object): Observable<T> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/merge-patch+json'
      })
    };

    return this.http.patch(`${this.resourceUrl}/${id}`, data, httpOptions) as Observable<T>;
  }

  protected restReplace<T>(id: string, data: T): Observable<T> {
    return this.http.put(`${this.resourceUrl}/${id}`, data) as Observable<T>;
  }

  protected restDelete(id: string): Observable<null> {
    return this.http.delete(`${this.resourceUrl}/${id}`).pipe(
      map(value => null)
    );
  }

  getResourceCollection(filters: Record<string, string|number|boolean> = {}, suffix:string = ''): Observable<ResourceCollection<any>> {
    return this.http.get<ResourceCollection<any>>(this.resourceUrl + suffix + this.formatQueryString(filters)).pipe(
      map(response => new HydraCollection<any>(response)),
      map(collection => ResourceCollectionFactory.createFromHydraCollection<any>(collection))
    );
  }

  protected formatQueryString(parameters: Record<string, string|number|boolean>): string {
    const urlSearchParams = new URLSearchParams();

    for (const parametersKey in parameters) {
      if (parameters.hasOwnProperty(parametersKey)) {
        const value = parameters[parametersKey];
        if (Array.isArray(value)) {
          value.forEach(v => {
            urlSearchParams.append(parametersKey, v.toString());
          });
        } else {
          urlSearchParams.append(parametersKey, value.toString());
        }
      }
    }

    const queryString = urlSearchParams.toString();
    return queryString ? '?' + queryString : '';
  }
}
